import {
  Blocks, Boxes, Building2, Bus, School, BriefcaseBusiness, ChevronUp, CreditCard, LogOut, Menu, Plus, Settings, UserRound, UsersRound, X
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { useApp } from "../context/AppContext";
import { btpModule } from "../modules/btp/module";
import { useFormationAccess } from "../modules/formation/access";
import { formationNavigation } from "../modules/formation/navigation";
import type { ModuleNavItem } from "../modules/types";
import { tabs as transportTabs } from "../modules/transport/common";

function initials(firstName?: string, lastName?: string, fallback?: string) {
  const parts = [firstName, lastName].map((value) => String(value || "").trim()).filter(Boolean);
  if (parts.length) return parts.slice(0, 2).map((value) => value[0]?.toUpperCase()).join("");
  const base = String(fallback || "ST").trim();
  const words = base.split(/[\s@._-]+/).filter(Boolean);
  return words.slice(0, 2).map((value) => value[0]?.toUpperCase()).join("") || "ST";
}

function SidebarLink({
  to, label, icon: Icon, end, onClick
}: {
  to: string; label: string; icon: ModuleNavItem["icon"]; end?: boolean; onClick: () => void;
}) {
  return <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({isActive}) =>
      "flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-semibold transition " +
      (isActive ? "bg-white text-slate-950 shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white")
    }
  ><Icon size={17}/><span>{label}</span></NavLink>;
}

export function Shell() {
  const { bootstrap, logout, switchOrganisation } = useApp();
  const { level: formationLevel, available: formationAvailable } = useFormationAccess();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [switchError, setSwitchError] = useState("");

  const activeCodes = new Set((bootstrap?.navigation || []).map((item) => item.code.toUpperCase()));
  const formationEnabled = Boolean(bootstrap?.business_app_access?.formation);
  const schoolLinked = activeCodes.has("ECOLE");
  const projectsEnabled = activeCodes.has("PROJECTS");
  const btpEnabled = activeCodes.has("BTP")
    && (!bootstrap?.membership?.btp || bootstrap.membership.btp.capabilities.length > 0);
  const role = bootstrap?.membership?.role || "";
  const canManageSubscription = ["OWNER", "ADMIN"].includes(role) || Boolean(bootstrap?.user.is_superuser);
  const btpCapabilities = new Set(bootstrap?.membership?.btp?.capabilities || []);
  const btpItems = btpModule.nav.filter((item) =>
    item.to === "/btp/profils" ? canManageSubscription :
      item.to === "/btp/devis" ? btpCapabilities.has("quotes") : true
  );
  const btpAccountsEnabled = canManageSubscription || btpCapabilities.has("pay");
  const transport = bootstrap?.membership?.transport;
  const transportEnabled = Boolean(transport?.capabilities.length && (transport.active || transport.continuity));
  const transportItems = transportTabs.filter(item => transport?.capabilities.includes(item.cap) || (item.cap === "board" && transport?.capabilities.includes("manifest")) || (item.cap === "sell" && transport?.capabilities.includes("finance")));

  const formationItems = formationEnabled
    ? formationNavigation.filter((item) => formationAvailable(item.feature))
    : [];
  const formationBusiness = formationItems.filter((item) => item.group === "business");
  const formationFinance = formationItems.filter((item) => item.group === "finance");

  const user = bootstrap?.user;
  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.email || user?.username || "Utilisateur";
  const organisationName = bootstrap?.active_organisation?.nom || "Votre espace";
  const avatar = initials(user?.first_name, user?.last_name, organisationName);

  function go(path: string) {
    setAccountOpen(false);
    setMobileOpen(false);
    navigate(path);
  }

  const closeMobile = () => setMobileOpen(false);

  const sidebar = (
    <div className="flex h-full min-h-0 flex-col bg-slate-950 text-white">
      <div className="shrink-0 border-b border-white/10 px-5 py-4">
        <div className="inline-flex rounded-xl bg-white px-2 py-1">
          <img src="/saheltech-mark.svg" alt="SahelTech — L’innovation à la source" className="h-10 w-auto" />
        </div>
        <div className="mt-2 truncate text-xs font-semibold text-slate-400">{organisationName}</div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {schoolLinked && <div className="mb-6">
          <div className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Applications indépendantes</div>
          <a href="https://ecole.saheltech.tech" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-semibold text-slate-300 hover:bg-white/10 hover:text-white">
            <School size={17}/><span>École · Gestion scolaire ↗</span>
          </a>
        </div>}
        {transportEnabled && <div className="mb-6"><div className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Transport</div><div className="space-y-0.5">{transportItems.map(item => <SidebarLink key={item.to} {...item} icon={Bus} onClick={closeMobile} />)}</div></div>}
        {formationEnabled && (
          <div>
            <div className="mb-2 flex items-center justify-between px-3">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Formation</span>
              <span className="rounded-md bg-white/10 px-1.5 py-0.5 text-[9px] font-black tracking-wide text-[#F3A34F]">{formationLevel}</span>
            </div>
            <div className="space-y-0.5">
              {formationBusiness.map((item) => <SidebarLink key={item.to} {...item} onClick={closeMobile}/>)}
            </div>

            {formationFinance.length > 0 && (
              <div className="mt-5">
                <div className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Finance</div>
                <div className="space-y-0.5">
                  {formationFinance.map((item) => <SidebarLink key={item.to} {...item} onClick={closeMobile}/>)}
                </div>
              </div>
            )}
          </div>
        )}

        <div className={formationEnabled ? "mt-6 border-t border-white/10 pt-5" : ""}>
          <div className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Relations</div>
          <div className="space-y-0.5">
            <SidebarLink to="/tiers" label="Tiers" icon={UsersRound} onClick={closeMobile}/>
          </div>
        </div>

        {projectsEnabled && (
          <div className="mt-6 border-t border-white/10 pt-5">
            <div className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Projets</div>
            <div className="space-y-0.5">
              <SidebarLink to="/projects" label="Projets" icon={Boxes} onClick={closeMobile}/>
              <SidebarLink to="/templates" label="Templates" icon={Blocks} onClick={closeMobile}/>
            </div>
          </div>
        )}

        {btpEnabled && (
          <div className="mt-6 border-t border-white/10 pt-5">
            <div className="mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">BTP</div>
            <div className="space-y-0.5">
              {btpItems.map((item) => <SidebarLink key={item.to} {...item} onClick={closeMobile} />)}
              {btpAccountsEnabled && <SidebarLink to="/finance/comptes" label="Comptes" icon={CreditCard} onClick={closeMobile} />}
            </div>
          </div>
        )}

      </nav>

      <div className="relative shrink-0 border-t border-white/10 p-3">
        {accountOpen && (
          <div className="absolute bottom-[calc(100%-2px)] left-3 right-3 z-50 mb-2 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 text-slate-900 shadow-2xl">
            <button onClick={() => go("/entreprises")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-slate-50"><Building2 size={17}/> Mes entreprises ({bootstrap?.organisations.length || 0})</button>
            {bootstrap?.identity_federated && <button onClick={() => go("/create-business")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-slate-50"><Plus size={17}/> Créer une entreprise</button>}
            <button onClick={() => go("/services-commerciaux")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-slate-50"><BriefcaseBusiness size={17}/> Commerce des services SahelTech</button>
            <button onClick={() => go("/settings?section=account")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-slate-50"><UserRound size={17}/> Mon profil</button>
            <button onClick={() => go("/settings")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-slate-50"><Settings size={17}/> Paramètres</button>
            {canManageSubscription && <button onClick={() => go("/settings?section=subscription")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-slate-50"><CreditCard size={17}/> Abonnement</button>}
            <div className="my-1 border-t border-slate-100"/>
            <button onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-600 hover:bg-red-50"><LogOut size={17}/> Déconnexion</button>
          </div>
        )}

        <button onClick={() => setAccountOpen((value) => !value)} className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition hover:bg-white/10">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#F3A34F] text-sm font-black text-slate-950">{avatar}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold text-white">{displayName}</span>
            <span className="block truncate text-[11px] font-medium text-slate-400">{organisationName}</span>
          </span>
          <ChevronUp size={16} className={"text-slate-400 transition " + (accountOpen ? "rotate-180" : "")}/>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setMobileOpen(false)}/>
          <aside className="relative h-full w-72 shadow-2xl">
            <button className="absolute right-3 top-3 z-10 rounded-xl bg-white/10 p-2 text-white" onClick={() => setMobileOpen(false)} aria-label="Fermer le menu"><X size={20}/></button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:px-8">
          <button className="rounded-xl border border-slate-200 p-2 lg:hidden" onClick={() => setMobileOpen(true)}><Menu size={20}/></button>
          <div className="flex min-w-0 items-center gap-2">
            {bootstrap && bootstrap.organisations.length > 1 ? (
              <select aria-label="Entreprise active" value={bootstrap.active_organisation?.code || ""}
                onChange={async (event) => {
                  setSwitchError("");
                  try {
                    await switchOrganisation(event.target.value);
                    navigate("/");
                  } catch (cause) {
                    setSwitchError(cause instanceof Error ? cause.message : "Accès à cette entreprise refusé.");
                  }
                }} className="field max-w-44 sm:max-w-64">
                {bootstrap.organisations.map((org) => <option key={org.code} value={org.code}>{org.nom}</option>)}
              </select>
            ) : <span className="truncate text-sm font-bold text-slate-700">{organisationName}</span>}
            <button onClick={() => go("/entreprises")} className="rounded-xl p-2 text-slate-700 hover:bg-slate-100" aria-label="Mes entreprises" title="Mes entreprises"><Building2 size={19}/></button>
            {bootstrap?.identity_federated && <button onClick={() => go("/create-business")} className="rounded-xl p-2 text-emerald-700 hover:bg-emerald-50" aria-label="Créer une entreprise" title="Créer une entreprise"><Plus size={19}/></button>}
          </div>
          {switchError && <p role="alert" className="max-w-xs text-xs text-red-700">{switchError}</p>}
        </header>
        <main className="mx-auto max-w-[1600px] p-4 lg:p-8"><Outlet key={bootstrap?.active_organisation?.code || ""}/></main>
      </div>
    </div>
  );
}
